import { useEffect, useId, useMemo, useState } from "react";
import Select from "react-select";
import { useControlHost } from "../components/inputs/useControlHost";
import { SelectMenuPortal } from "../components/inputs/SelectMenuPortal";
import { useOutsideDismiss } from "../components/useOutsideDismiss";
import { request } from "../api/client";
import { useLocale } from "../i18n";
import "flag-icons/css/flag-icons.min.css";

type Country = { iso2: string; dialCode: string };
export function usePhoneCountries() {
  const [countries, setCountries] = useState<Country[]>([{ iso2: "JO", dialCode: "+962" }]);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setFailed(false);
    request<{ items: Country[] }>("/auth/countries").then(result => {
      if (!Array.isArray(result.items) || !result.items.some(c => c.iso2 === "JO") || result.items.length > 300 ||
          result.items.some(c => !/^[A-Z]{2}$/.test(c.iso2) || !/^\+[1-9][0-9]{0,3}$/.test(c.dialCode))) throw new Error("Invalid countries");
      if (active) setCountries(result.items);
    }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [attempt]);
  return { countries, failed, retry: () => setAttempt(n => n + 1) };
}

export function PhoneCountrySelect({ value, onChange, disabled, countries }: {
  value: string; onChange: (value: string) => void; disabled: boolean; countries: Country[];
}) {
  const { lang, w } = useLocale();
  const id = useId();
  const host = useControlHost(disabled);
  const [menuOpen, setMenuOpen] = useState(false);
  useOutsideDismiss(host.ref, menuOpen, () => setMenuOpen(false), id);
  const options = useMemo(() => {
    const names = new Intl.DisplayNames([lang], { type: "region" });
    return countries.map(c => ({ ...c, value: c.iso2, label: `${names.of(c.iso2)} ${c.dialCode}`, name: names.of(c.iso2) }))
      .sort((a, b) => a.iso2 === "JO" ? -1 : b.iso2 === "JO" ? 1 : a.label.localeCompare(b.label, lang));
  }, [countries, lang]);
  return <div ref={host.ref} className="entry-country-field">
    <Select<(typeof options)[number], false> inputId={id} instanceId={id} className="mzn-select" classNamePrefix="mzn-select"
      aria-label={w("الدولة ورمز الاتصال", "Country and calling code")}
      components={{ IndicatorSeparator: null, MenuPortal: SelectMenuPortal }}
      menuPortalTarget={host.portal} menuPosition="fixed" menuPlacement="auto"
      menuIsOpen={menuOpen} onMenuOpen={() => setMenuOpen(true)} onMenuClose={() => setMenuOpen(false)}
      styles={{ menuPortal: base => ({ ...base, zIndex: 1000 }) }}
      options={options} value={options.find(c => c.iso2 === value)} isDisabled={host.disabled} isRtl={lang === "ar"}
      isSearchable maxMenuHeight={240} onChange={country => { if (country && !host.isLocked()) onChange(country.iso2); }}
      noOptionsMessage={() => w("لا توجد دولة مطابقة", "No matching country")}
      formatOptionLabel={(country, { context }) => <span className={`entry-country-option${context === "menu" ? " entry-country-option-row" : ""}`} title={country.label}><span className={`fi fi-${country.iso2.toLowerCase()}`} aria-hidden="true" />
        {context === "menu" && <span className="entry-country-name">{country.name}</span>}<bdi dir="ltr">{country.dialCode}</bdi></span>} />
  </div>;
}
